var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { c as compareIgnoreCase, s as strEndsWith } from "../CommonStringUtils-8860a016.js";
import parseURL from "url-parse";
import { parse } from "query-string";
import { EXP_QUERY_STRINGS, BASE_URL_FIELD } from "../constants/QueryString.constants.js";
import { UI_ORIGINS } from "../host/Host.constants.js";
import EnvironmentService from "./EnvironmentService.js";
class NavigationService {
  constructor() {
    __publicField(this, "history");
    /**
     * Regex path matching in React Router
     */
    __publicField(this, "_match", { path: "", url: "" });
    __publicField(this, "addParamToPath", (payload) => {
      const { url, param, value } = payload;
      const location = parseURL(url);
      const { pathname, query, hash } = location;
      const result = this.addParamToQuery({ url: `${pathname}${query}`, param, value });
      return `${pathname}${query}${result}${hash}`;
    });
    /**
     * add multiple query params to path (similar to addParamToPath but handles an array of query params)
     * @param payload url and query params array to add to path
     * @returns path with query params and hash appended
     */
    __publicField(this, "addParamsToPath", (payload) => {
      const { url, queryParams } = payload;
      const location = parseURL(url);
      const { pathname, query, hash } = location;
      const queryParamsToBeAdded = queryParams.map(
        (queryParam) => this.addParamToQuery({ url: `${pathname}${query}`, param: queryParam.param, value: queryParam.value })
      );
      let result = `${pathname}${query}`;
      queryParamsToBeAdded.forEach((queryParam) => {
        result += queryParam;
      });
      return `${result}${hash}`;
    });
    /**
     * Use the below method when you want to persist the full url
     * example href: https://exp.req.url/abc/def?p=1&q=2  param : r, value : 3
     * will get converted to https://exp.req.url/abc/def?p=1&q=2&r=3
     *
     * Note: this will not override an existing value, you must first call omitQueryParams (these methods should be redeveloped)
     *
     * if you don't want to perist the base url, use addParamToPath
     */
    __publicField(this, "addParamToHref", (payload) => {
      const { href, param, value } = payload;
      const location = parseURL(href);
      const { hash } = location;
      const search = this.addParamToQuery({ url: href.replace(hash, ""), param, value });
      return `${href.replace(hash, "")}${search}${hash}`;
    });
    __publicField(this, "addParamToQuery", (payload) => {
      const { url, param, value } = payload;
      let search = (payload == null ? void 0 : payload.search) || "";
      let result = "";
      if (!!value && !url.includes(param) && !search.includes(param)) {
        result += url.includes("?") || search.includes("?") ? "&" : "?";
        result += `${param}=${value}`;
      }
      return `${result}`;
    });
    this.history = null;
  }
  setHistory(history) {
    this.history = history;
  }
  setRoot(match, documentTitle) {
    if (!match)
      match = { path: "", url: "" };
    EnvironmentService.title = documentTitle;
    this._match = match;
  }
  get root() {
    return this._match || { path: "", url: "" };
  }
  /**
   * Navigates to another page locally
   * @param path
   */
  navigateTo(path, target) {
    let location = document.location;
    path = this.getURLPathName(path);
    if (!!target) {
      window.open(path, target);
      return;
    }
    if (path !== location.href && path !== location.pathname + location.search) {
      this.history.push(path);
    }
  }
  /**
   * For click events, uses local navigation to navigate to the path
   * For ctrl + click events, opens the path in a new tab
   * @param event
   * @param path
   */
  handleLinkClickNavigation(event, path) {
    if (event == null ? void 0 : event.ctrlKey) {
      this.navigateTo(path, "_blank");
    } else {
      this.navigateTo(path);
    }
  }
  /**
   * Force links to open in new tab
   * @param path
   */
  openLinkInNewTab(path) {
    this.navigateTo(path, "_blank");
  }
  /**
   * Note: Do not use
   * Used to reload the page in the current tab
   * Predominently this is a hack to "reset" the current application state
   * @param path
   */
  openLinkInSameTab(path) {
    this.navigateTo(path, "_self");
  }
  /**
   * External links will open in a new tab and not persist global query params, these must be provided
   * @param path
   */
  externalLink(path) {
    window.open(path, "_blank");
  }
  /**
   * Used in specific cases where we want to allow an external link to open in the current tab
   * This method should not be used for internal links that should persist global query params
   * @param path
   */
  openExternalLinkInCurrentTab(path) {
    window.open(path, "_self");
  }
  handleExternalLinkClickNavigation(event, path) {
    if (event == null ? void 0 : event.ctrlKey) {
      this.externalLink(path);
    } else {
      this.openExternalLinkInCurrentTab(path);
    }
  }
  /**
   * DO NOT USE - added to support legacy pages where customers were directed to a new tab / window
   * E.g. resolve gate for an experiment
   */
  closePageOpenedFromAnotherTab() {
    if (window.opener && !window.opener.closed) {
      window.opener.location.reload();
      window.close();
    }
  }
  /**
   * Navigates to BCT pages
   * @param path
   */
  navigateBCT(path) {
    let location = document.location;
    path = this.getURLPathName(path);
    if (path !== location.href && path !== location.pathname + location.search) {
      window.location.href = `${window.location.origin}${path}`;
    }
  }
  /**
   * Redirect to absolute URL
   * @param url
   * @param sanitize persist only expected global search params
   * @param {Location} location [window.location] provide Location object (used for testing)
   */
  redirectToURL(url, sanitize = false, location = window.location) {
    const { pathname, search, hash } = location;
    const { origin } = parseURL(url);
    const queryParams = !!sanitize ? this.getCustomQueryParams("", search) : search;
    const href = `${origin}${pathname}${queryParams}${hash}`;
    window.location.href = `${href}`;
    return href;
  }
  getCustomQueryParamsIncludingSomeParams(path, include, search = window.location.search) {
    let additionalUrlParam = "";
    const queryParams = parse(search);
    if (!!queryParams) {
      Object.entries(queryParams).reverse().forEach((entry) => {
        let [param, value] = entry;
        if (!![...EXP_QUERY_STRINGS, ...include].filter((expParam) => compareIgnoreCase(expParam, param)).length) {
          if (!!value && compareIgnoreCase(param, BASE_URL_FIELD)) {
            if (!strEndsWith(value, "/")) {
              value += "/";
            }
          }
          additionalUrlParam += this.addParamToQuery({ url: path, search: additionalUrlParam, param, value });
        }
      });
    }
    return `${path}${additionalUrlParam}`;
  }
  /**
   * Persist custom query parameters while navigating
   * @param {string} path
   * @param {string} search [window.location.search] query param string (used for testing)
   */
  getCustomQueryParams(path, search = window.location.search) {
    return this.getCustomQueryParamsIncludingSomeParams(path, [], search);
  }
  /**
   * Get query parameters from current url
   * @param {string} param
   * @param {string} search [window.location.search] query param string (used for testing)
   */
  getQueryParamValue(param, search = window.location.search) {
    const queryParams = parse(search);
    if (!!queryParams) {
      const queryParam = Object.entries(queryParams).find((entry) => {
        let [paramName, value] = entry;
        return paramName == param;
      });
      return !!queryParam ? queryParam[1] : void 0;
    }
    return void 0;
  }
  /**
   * Persist custom query parameters while navigating
   * @param {string} path
   * @param {string[]} omit omit these parameter from url
   * @param {string} search [window.location.search] query param string (used for testing)
   */
  getCustomQueryParamsOmitingSomeParams(path, omit, search = window.location.search) {
    return this.getCustomQueryParams(path, this.omitQueryParams(omit, search));
  }
  omitQueryParams(omit, search = window.location.search) {
    let searchParts = [];
    const params = parse(search);
    let entries = Object.entries(params);
    if (!!omit.length) {
      entries = entries.reverse();
    }
    entries.forEach((entry) => {
      let [param, value] = entry;
      if (!omit.filter((item) => compareIgnoreCase(item, param)).length) {
        searchParts.push(`${param}=${value}`);
      }
    });
    return !!searchParts.length ? `?${searchParts.join("&")}` : ``;
  }
  getCurrentDocumentLocation() {
    let location = document.location;
    return location;
  }
  /**
   *
   * @param paramKey url parameter that you want to retrieve
   * @param locationSearch window.location.search
   */
  getUrlParam(paramKey, locationSearch = window.location.search) {
    const urlParams = new URLSearchParams(locationSearch);
    return urlParams.get(paramKey);
  }
  /**
   * Get the full page pathname + search + hash
   * Usage: used to persist the URL, possibly append additional query params
   * @param path
   */
  getCompletePath() {
    const pathname = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;
    return `${pathname}${search}${hash}`;
  }
  /**
   * Get the current URL path
   * Usage: Used to navigate within the ExpUI Host and render the BCT page in an iFrame
   * @param path
   */
  getURLPathName(path) {
    const location = parseURL(path);
    const { pathname, query, hash, origin } = location;
    if (!UI_ORIGINS.includes(origin))
      return path;
    return `${pathname}${this.getCustomQueryParams(query)}${hash}`;
  }
}
const navigationService = new NavigationService();
export {
  NavigationService,
  navigationService as default
};
//# sourceMappingURL=NavigationService.js.map
